import React from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

export type TeamMemberInput = {
  id?: number;
  name: string;
  farmTeamRoleId: number | ''; // '' while empty
};

export type TeamSubmission = {members: TeamMemberInput[]};

export function FdTeamForm(props: {
  roles: {id: number; name: string}[];
  defaultMembers: TeamMemberInput[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (d: TeamSubmission) => void;
  onCancel: () => void;
  submitErrors?: string[];
}) {
  /* ───────── RHF ───────── */
  const {
    control,
    handleSubmit,
    formState: {errors},
    register,
  } = useForm<TeamSubmission>({
    defaultValues: {members: props.defaultMembers},
  });
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'members',
    keyName: 'fieldKey',
  });

  /* ───────── markup ───────── */
  return (
    <div className="generic-form-container fd-team-form">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(props.onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12}>
              <Typography variant="subtitle2">Team Members</Typography>
            </Grid>

            {/* Display submit errors */}
            {props.submitErrors && props.submitErrors.length > 0 && (
              <Grid item xs={12}>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #dc3545',
                    borderRadius: '4px',
                    marginBottom: '16px',
                  }}
                >
                  {props.submitErrors.map((error, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      style={{color: '#721c24', marginBottom: '4px'}}
                    >
                      {error}
                    </Typography>
                  ))}
                </div>
              </Grid>
            )}

            {fields.map((f, idx) => (
              <React.Fragment key={f.fieldKey}>
                <input
                  type="hidden"
                  {...register(`members.${idx}.id` as const)}
                  defaultValue={f.id ?? ''}
                />
                {/* name */}
                <Grid item xs={12} sm={5}>
                  <Controller
                    name={`members.${idx}.name`}
                    control={control}
                    rules={{required: 'Name required'}}
                    render={({field}) => (
                      <TextField
                        {...field}
                        label="Full name"
                        variant="outlined"
                        fullWidth
                        error={!!errors.members?.[idx]?.name}
                        helperText={errors.members?.[idx]?.name?.message}
                      />
                    )}
                  />
                </Grid>

                {/* role */}
                <Grid item xs={12} sm={5}>
                  <Controller
                    name={`members.${idx}.farmTeamRoleId`}
                    control={control}
                    rules={{
                      validate: (v) => (v === '' ? 'Role required' : true),
                    }}
                    render={({field}) => (
                      <TextField
                        {...field}
                        select
                        label="Role"
                        variant="outlined"
                        fullWidth
                        error={!!errors.members?.[idx]?.farmTeamRoleId}
                        helperText={
                          errors.members?.[idx]?.farmTeamRoleId?.message
                        }
                      >
                        <MenuItem value="" disabled />
                        {props.roles.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* remove */}
                <Grid
                  item
                  xs={12}
                  sm={2}
                  style={{display: 'flex', alignItems: 'center'}}
                >
                  <IconButton onClick={() => remove(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}

            {/* add button */}
            <Grid item xs={12}>
              <IconButton
                onClick={() =>
                  append({id: undefined, name: '', farmTeamRoleId: ''})
                }
                style={{marginLeft: -12}}
                title="Add team member"
                aria-label="Add team member"
              >
                <AddIcon />
              </IconButton>
              <Typography
                variant="caption"
                style={{marginLeft: '8px', color: '#666'}}
              >
                Click to add a new team member
              </Typography>
            </Grid>
          </Grid>

          {/* actions */}
          <Grid container spacing={2} style={{marginTop: '1rem'}}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={props.onCancel}
                disabled={props.isLoading}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={props.isLoading}
              >
                {props.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  props.submitButtonTitle
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
